import React, { useCallback, useEffect, useState } from "react";

import { Alert, Text } from "@catchoftheday/cg-components";
import { cancellablePromise, noop } from "@catchoftheday/utilities";

import {
  BannerProps,
  getCountryDataMessage,
  getOnePassMigrationMessage,
} from "./message-handlers";

type FlashMessageBannerProps = {
  isMicrofrontend?: boolean;
};

type FlashBannerProps = {
  key: string;
} & BannerProps;

export const FlashMessageBanner = ({
  isMicrofrontend = false,
}: FlashMessageBannerProps) => {
  const [banners, setBanners] = useState<FlashBannerProps[]>([]);

  const updateBannersList = useCallback(
    (key: string, bannerProps: BannerProps | null) => {
      setBanners((prevBanners) => {
        let banners = [...prevBanners];
        const indexOfKey = banners.findIndex((banner) => banner.key === key);
        if (indexOfKey >= 0) {
          if (bannerProps) {
            banners[indexOfKey] = {
              key,
              ...bannerProps,
            };
          } else {
            banners = banners.filter((banner) => banner.key !== key);
          }
        } else if (bannerProps) {
          banners.push({
            key,
            ...bannerProps,
          });
        }
        return banners;
      });
    },
    []
  );

  const getCountryMessagePromise = useCallback(async () => {
    return await getCountryDataMessage(isMicrofrontend).then((data) => data);
  }, [isMicrofrontend]);

  useEffect(() => {
    const countryMessagePromise = getCountryMessagePromise();
    const { promise, cancel } = cancellablePromise(countryMessagePromise);
    promise
      .then((d) => {
        updateBannersList("country", d);
      })
      .catch(noop);
    return cancel;
  }, [getCountryMessagePromise, updateBannersList]);

  useEffect(() => {
    const onePassMigrationMessage = getOnePassMigrationMessage(isMicrofrontend);
    updateBannersList("onePassMigration", onePassMigrationMessage);
  }, [isMicrofrontend, updateBannersList]);

  return (
    <>
      {banners.map(({ key, theme, variant, showIcon, message }) => (
        <Alert
          key={`flash-banner-${key}`}
          type="inline"
          theme={theme}
          variant={variant}
          showAlertIcon={showIcon ?? false}
          showCloseButton
          hideAlert={() => updateBannersList(key, null)}
        >
          <Text
            fontSize={{ xs: "sm", md: "base" }}
            fontWeight={400}
            my={0}
            mx={{ lg: "auto" }}
          >
            {message}
          </Text>
        </Alert>
      ))}
    </>
  );
};
